import { useCallback, useEffect, useRef, useState } from "react";

export type AsyncStatus = "idle" | "loading" | "success" | "error";

export interface AsyncState<T> {
  status: AsyncStatus;
  data: T | null;
  error: string;
  /** Ключ запроса, для которого получены `data` (защита от устаревших данных). */
  key: string | null;
  reload: () => void;
}

/**
 * Загружает данные для ключа `key` (обычно сериализованные фильтры).
 * Новый ключ отменяет предыдущий запрос; ответ устаревшего запроса отбрасывается.
 * `data` сбрасывается при смене ключа, поэтому старые строки не остаются
 * под новым запросом. `key = null` — запрос не выполняется.
 */
export function useAsync<T>(
  key: string | null,
  fetcher: (signal: AbortSignal) => Promise<T>,
): AsyncState<T> {
  const [state, setState] = useState<{
    status: AsyncStatus;
    data: T | null;
    error: string;
    key: string | null;
  }>({ status: "idle", data: null, error: "", key: null });
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (key === null) {
      setState({ status: "idle", data: null, error: "", key: null });
      return;
    }
    const controller = new AbortController();
    // Повторная загрузка того же ключа не стирает показанные данные.
    setState((prev) => ({
      status: "loading",
      data: prev.key === key ? prev.data : null,
      error: "",
      key,
    }));
    fetcherRef
      .current(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ status: "success", data, error: "", key });
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        if (e instanceof DOMException && e.name === "AbortError") return;
        setState({
          status: "error",
          data: null,
          error: e instanceof Error ? e.message : String(e),
          key,
        });
      });
    return () => controller.abort();
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}
